import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Search, X, ChevronDown, ChevronUp, Filter } from 'lucide-react';
import { useAssetClassesDropdown } from '@/api/hooks/useCatalogs';
import {
  useMetadataSchemaForAssetClass,
  useSearchAssetsByMetadata,
} from '@/api/hooks/useMetadata';
import type { MetadataSchemaField, MetadataSearchCriteria } from '@/types/metadata.types';

interface MetadataSearchPanelProps {
  onSearch: (assetIds: string[]) => void;
  onClear: () => void;
  isActive?: boolean;
}

interface FieldValue {
  fieldKey: string;
  value: string | number | boolean | string[];
}

export function MetadataSearchPanel({ onSearch, onClear, isActive = false }: MetadataSearchPanelProps) {
  const { t } = useTranslation(['metadata', 'common']);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedAssetClassId, setSelectedAssetClassId] = useState<number | null>(null);
  const [fieldValues, setFieldValues] = useState<FieldValue[]>([]);
  const [searchResults, setSearchResults] = useState<string[] | null>(null);

  // Fetch asset classes for dropdown
  const { data: assetClasses, isLoading: assetClassesLoading } = useAssetClassesDropdown();

  // Fetch schema for selected asset class
  const { data: schema, isLoading: schemaLoading } = useMetadataSchemaForAssetClass(
    selectedAssetClassId || 0,
    !!selectedAssetClassId
  );

  // Search mutation
  const searchMutation = useSearchAssetsByMetadata();

  // Reset field values when asset class changes
  useEffect(() => {
    setFieldValues([]);
    setSearchResults(null);
  }, [selectedAssetClassId]);

  // Get searchable fields from schema
  const searchableFields = schema?.fields.filter((f) => f.showInSearch && f.isActive) || [];

  // Update field value
  const updateFieldValue = (fieldKey: string, value: string | number | boolean | string[]) => {
    setFieldValues((prev) => {
      const existing = prev.findIndex((fv) => fv.fieldKey === fieldKey);
      if (existing >= 0) {
        const updated = [...prev];
        updated[existing] = { fieldKey, value };
        return updated;
      }
      return [...prev, { fieldKey, value }];
    });
  };

  // Get current value for a field
  const getFieldValue = (fieldKey: string): string | number | boolean | string[] | undefined => {
    return fieldValues.find((fv) => fv.fieldKey === fieldKey)?.value;
  };

  // Clear all filters
  const handleClear = () => {
    setSelectedAssetClassId(null);
    setFieldValues([]);
    setSearchResults(null);
    onClear();
  };

  // Execute search
  const handleSearch = async () => {
    if (!selectedAssetClassId || fieldValues.length === 0) return;

    const criteria: MetadataSearchCriteria = {};
    fieldValues.forEach((fv) => {
      if (fv.value !== '' && fv.value !== undefined) {
        criteria[fv.fieldKey] = fv.value;
      }
    });

    if (Object.keys(criteria).length === 0) return;

    try {
      const assetIds = await searchMutation.mutateAsync({ criteria });
      setSearchResults(assetIds);
      onSearch(assetIds);
    } catch (error) {
      console.error('Metadata search failed:', error);
    }
  };

  // Count active filters
  const activeFilterCount = fieldValues.filter((fv) => fv.value !== '' && fv.value !== undefined).length;

  // Render field input based on field type
  const renderFieldInput = (field: MetadataSchemaField) => {
    const value = getFieldValue(field.fieldKey);

    switch (field.fieldTypeCode) {
      case 'TEXT':
      case 'TEXTAREA':
        return (
          <Input
            id={`search-${field.fieldKey}`}
            placeholder={field.placeholder || `Search ${field.label}...`}
            value={(value as string) || ''}
            onChange={(e) => updateFieldValue(field.fieldKey, e.target.value)}
          />
        );

      case 'NUMBER':
      case 'DECIMAL':
        return (
          <Input
            id={`search-${field.fieldKey}`}
            type="number"
            placeholder={field.placeholder || `Enter ${field.label}...`}
            value={(value as number) || ''}
            onChange={(e) =>
              updateFieldValue(field.fieldKey, e.target.value ? parseFloat(e.target.value) : '')
            }
          />
        );

      case 'DATE':
      case 'DATETIME':
        return (
          <Input
            id={`search-${field.fieldKey}`}
            type="date"
            value={(value as string) || ''}
            onChange={(e) => updateFieldValue(field.fieldKey, e.target.value)}
          />
        );

      case 'DROPDOWN':
        return (
          <Select
            value={(value as string) || 'all'}
            onValueChange={(v) => updateFieldValue(field.fieldKey, v === 'all' ? '' : v)}
          >
            <SelectTrigger id={`search-${field.fieldKey}`}>
              <SelectValue placeholder={`Select ${field.label}...`} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('common:labels.all')}</SelectItem>
              {field.options
                ?.filter((o) => o.isActive)
                .map((option) => (
                  <SelectItem key={option.optionValue} value={option.optionValue}>
                    {option.optionLabel}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        );

      case 'MULTISELECT':
        // For multiselect, we'll use a simple dropdown for now (single selection for search)
        return (
          <Select
            value={(value as string) || 'all'}
            onValueChange={(v) => updateFieldValue(field.fieldKey, v === 'all' ? '' : v)}
          >
            <SelectTrigger id={`search-${field.fieldKey}`}>
              <SelectValue placeholder={`Select ${field.label}...`} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('common:labels.all')}</SelectItem>
              {field.options
                ?.filter((o) => o.isActive)
                .map((option) => (
                  <SelectItem key={option.optionValue} value={option.optionValue}>
                    {option.optionLabel}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        );

      case 'BOOLEAN':
        return (
          <Select
            value={value === undefined ? 'all' : value ? 'true' : 'false'}
            onValueChange={(v) =>
              updateFieldValue(field.fieldKey, v === 'all' ? '' : v === 'true')
            }
          >
            <SelectTrigger id={`search-${field.fieldKey}`}>
              <SelectValue placeholder={`Select ${field.label}...`} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('common:labels.all')}</SelectItem>
              <SelectItem value="true">{t('common:buttons.yes')}</SelectItem>
              <SelectItem value="false">{t('common:buttons.no')}</SelectItem>
            </SelectContent>
          </Select>
        );

      default:
        return (
          <Input
            id={`search-${field.fieldKey}`}
            placeholder={field.placeholder || `Search ${field.label}...`}
            value={(value as string) || ''}
            onChange={(e) => updateFieldValue(field.fieldKey, e.target.value)}
          />
        );
    }
  };

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger asChild>
        <Button
          variant={isActive || activeFilterCount > 0 ? 'secondary' : 'outline'}
          className="w-full justify-between"
        >
          <span className="flex items-center gap-2">
            <Filter className="h-4 w-4" />
            {t('metadataSearch.title')}
          </span>
          <span className="flex items-center gap-2">
            {activeFilterCount > 0 && (
              <Badge variant="secondary">{activeFilterCount}</Badge>
            )}
            {searchResults !== null && (
              <Badge variant="outline">
                {searchResults.length} {t('metadataSearch.results')}
              </Badge>
            )}
            {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </span>
        </Button>
      </CollapsibleTrigger>

      <CollapsibleContent className="mt-2">
        <div className="rounded-lg border bg-muted/50 p-4 space-y-4">
          {/* Asset Class Selection */}
          <div className="space-y-2">
            <Label htmlFor="search-asset-class">
              {t('metadataSearch.selectAssetClass')}
            </Label>
            {assetClassesLoading ? (
              <Skeleton className="h-10 w-full" />
            ) : (
              <Select
                value={selectedAssetClassId?.toString() || ''}
                onValueChange={(v) => setSelectedAssetClassId(v ? parseInt(v) : null)}
              >
                <SelectTrigger id="search-asset-class">
                  <SelectValue placeholder={t('metadataSearch.selectAssetClassPlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  {assetClasses?.map((ac) => (
                    <SelectItem key={ac.value} value={ac.value.toString()}>
                      {ac.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Loading schema */}
          {schemaLoading && selectedAssetClassId && (
            <div className="space-y-3">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-10 w-full" />
            </div>
          )}

          {/* Searchable Fields */}
          {schema && searchableFields.length > 0 && (
            <ScrollArea className="max-h-[300px]">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {searchableFields.map((field) => (
                  <div key={field.fieldKey} className="space-y-1.5">
                    <Label htmlFor={`search-${field.fieldKey}`} className="text-sm">
                      {field.label}
                    </Label>
                    {renderFieldInput(field)}
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}

          {/* No searchable fields message */}
          {schema && searchableFields.length === 0 && (
            <div className="text-center py-4 text-sm text-muted-foreground">
              {t('metadataSearch.noSearchableFields')}
            </div>
          )}

          {/* No schema message */}
          {selectedAssetClassId && !schemaLoading && !schema && (
            <div className="text-center py-4 text-sm text-muted-foreground">
              {t('metadataSearch.noSchema')}
            </div>
          )}

          {/* Action Buttons */}
          {selectedAssetClassId && searchableFields.length > 0 && (
            <div className="flex items-center justify-end gap-2 pt-2 border-t">
              <Button variant="ghost" size="sm" onClick={handleClear}>
                <X className="mr-2 h-4 w-4" />
                {t('common:buttons.clear')}
              </Button>
              <Button
                size="sm"
                onClick={handleSearch}
                disabled={activeFilterCount === 0 || searchMutation.isPending}
              >
                <Search className="mr-2 h-4 w-4" />
                {searchMutation.isPending
                  ? t('common:status.loading')
                  : t('common:buttons.search')}
              </Button>
            </div>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

export function MetadataSearchPanelSkeleton() {
  return (
    <div className="space-y-4">
      <Skeleton className="h-10 w-full" />
    </div>
  );
}
